import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';

// ─── Helper ──────────────────────────────────────────────────────────────────
function badge(u) {
  const now = new Date();
  if (!u.is_approved) return { label: 'Pending', cls: 'bg-yellow-100 text-yellow-700' };
  if (u.trial_expired) return { label: 'Expired', cls: 'bg-red-100 text-red-700' };
  if (u.account_type === 'paid') return { label: 'Paid', cls: 'bg-green-100 text-green-700' };
  if (u.trial_ends_at) {
    const days = Math.ceil((new Date(u.trial_ends_at) - now) / 86400000);
    return {
      label: `Trial (${days}d left)`,
      cls: days <= 7 ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700',
    };
  }
  return { label: 'Trial', cls: 'bg-blue-100 text-blue-700' };
}

function StatCard({ label, value, color }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-1 shadow-sm`}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

// ─── Create user modal ────────────────────────────────────────────────────────
function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', password: '',
    company: '', account_type: 'trial', trial_days: '30', notes: '',
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/admin/users', {
        ...form,
        trial_days: parseInt(form.trial_days),
      });
      toast.success(`Account created for ${res.data.first_name} ${res.data.last_name}`);
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto flex min-h-full items-start justify-center p-4 sm:items-center">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-lg font-bold text-gray-900">Create New User</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">✕</button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">First Name</label>
              <input className="input" value={form.first_name} onChange={set('first_name')} required placeholder="Jane" />
            </div>
            <div>
              <label className="label">Last Name</label>
              <input className="input" value={form.last_name} onChange={set('last_name')} required placeholder="Smith" />
            </div>
          </div>

          <div>
            <label className="label">Email</label>
            <input type="email" className="input" value={form.email} onChange={set('email')} required placeholder="jane@company.com" />
          </div>

          <div>
            <label className="label">Password</label>
            <input type="password" className="input" value={form.password} onChange={set('password')} required minLength={6} placeholder="min 6 characters" />
          </div>

          <div>
            <label className="label">Company <span className="text-gray-400 font-normal">(optional)</span></label>
            <input className="input" value={form.company} onChange={set('company')} placeholder="ABC Strata Management" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Account Type</label>
              <select className="input" value={form.account_type} onChange={set('account_type')}>
                <option value="trial">Trial</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            {form.account_type === 'trial' && (
              <div>
                <label className="label">Trial Length</label>
                <select className="input" value={form.trial_days} onChange={set('trial_days')}>
                  <option value="30">30 days</option>
                  <option value="60">60 days</option>
                  <option value="90">90 days</option>
                  <option value="180">180 days</option>
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="label">Notes <span className="text-gray-400 font-normal">(internal)</span></label>
            <textarea className="input resize-none" rows={2} value={form.notes} onChange={set('notes')} placeholder="Referral, context, etc." />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium text-sm">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm disabled:opacity-50">
              {loading ? 'Creating…' : 'Create & Send Welcome Email'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Edit user modal ──────────────────────────────────────────────────────────
function EditUserModal({ user, onClose, onUpdated, onDeleted }) {
  const [form, setForm] = useState({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    company: user.company || '',
    is_approved: user.is_approved,
    account_type: user.account_type || 'trial',
    extend_days: '',
    reset_password: '',
    notes: user.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const toggle = (k) => () => setForm(p => ({ ...p, [k]: !p[k] }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      first_name: form.first_name,
      last_name: form.last_name,
      company: form.company,
      is_approved: form.is_approved,
      account_type: form.account_type,
      notes: form.notes,
    };
    if (form.extend_days) payload.trial_days = parseInt(form.extend_days);
    if (form.reset_password) payload.reset_password = form.reset_password;

    try {
      const res = await api.put(`/admin/users/${user.id}`, payload);
      toast.success('User updated');
      onUpdated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete account for ${user.first_name} ${user.last_name}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success('User deleted');
      onDeleted(user.id);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const b = badge(user);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto flex min-h-full items-start justify-center p-4 sm:items-center">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{user.first_name} {user.last_name}</h2>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">✕</button>
        </div>

        {/* Usage stats */}
        <div className="px-6 pt-4 grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">Buildings</p>
            <p className="text-xl font-bold text-gray-900">{user.buildings_count ?? '—'}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">Work Orders</p>
            <p className="text-xl font-bold text-gray-900">{user.workorders_count ?? '—'}</p>
          </div>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">First Name</label>
              <input className="input" value={form.first_name} onChange={set('first_name')} required />
            </div>
            <div>
              <label className="label">Last Name</label>
              <input className="input" value={form.last_name} onChange={set('last_name')} required />
            </div>
          </div>

          <div>
            <label className="label">Company</label>
            <input className="input" value={form.company} onChange={set('company')} />
          </div>

          {/* Approved toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
            <div>
              <p className="text-sm font-medium text-gray-800">Account Approved</p>
              <p className="text-xs text-gray-500">Toggle to block/unblock access</p>
            </div>
            <button
              type="button"
              onClick={toggle('is_approved')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form.is_approved ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${
                form.is_approved ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* Account type */}
          <div>
            <label className="label">Account Type</label>
            <select className="input" value={form.account_type} onChange={set('account_type')}>
              <option value="trial">Trial</option>
              <option value="paid">Paid</option>
            </select>
          </div>

          {/* Extend trial */}
          <div>
            <label className="label">Extend Trial By <span className="text-gray-400 font-normal">(leave blank to keep current)</span></label>
            <select className="input" value={form.extend_days} onChange={set('extend_days')}>
              <option value="">— no change —</option>
              <option value="30">+ 30 days</option>
              <option value="60">+ 60 days</option>
              <option value="90">+ 90 days</option>
              <option value="180">+ 180 days</option>
            </select>
          </div>

          {/* Reset password */}
          <div>
            <label className="label">Reset Password <span className="text-gray-400 font-normal">(leave blank to keep current)</span></label>
            <input type="password" className="input" value={form.reset_password} onChange={set('reset_password')} placeholder="new password" minLength={6} />
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea className="input resize-none" rows={2} value={form.notes} onChange={set('notes')} />
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2.5 border border-red-200 text-red-600 hover:bg-red-50 rounded-xl font-medium text-sm disabled:opacity-50"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium text-sm">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm disabled:opacity-50">
              {loading ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Create Council Member modal ──────────────────────────────────────────────
function CreateCouncilModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '', council_building_id: '' });
  const [buildingList, setBuildingList] = useState([]);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    api.get('/admin/buildings').then((res) => setBuildingList(res.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.council_building_id) { toast.error('Select a building'); return; }
    setLoading(true);
    try {
      const res = await api.post('/admin/council-members', form);
      toast.success(`Council member created: ${res.data.first_name} ${res.data.last_name}`);
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create council member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto flex min-h-full items-start justify-center p-4 sm:items-center">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Add Council Member</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">✕</button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">First Name</label>
              <input className="input" value={form.first_name} onChange={set('first_name')} required placeholder="Jane" />
            </div>
            <div>
              <label className="label">Last Name</label>
              <input className="input" value={form.last_name} onChange={set('last_name')} required placeholder="Smith" />
            </div>
          </div>
          <div>
            <label className="label">Email</label>
            <input type="email" className="input" value={form.email} onChange={set('email')} required placeholder="jane@example.com" />
          </div>
          <div>
            <label className="label">Temporary Password</label>
            <input type="password" className="input" value={form.password} onChange={set('password')} required minLength={6} placeholder="min 6 characters" />
          </div>
          <div>
            <label className="label">Assigned Building</label>
            <select className="input" value={form.council_building_id} onChange={set('council_building_id')} required>
              <option value="">Select building…</option>
              {buildingList.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">Council members have read-only access to their assigned building.</p>
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium text-sm">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm disabled:opacity-50">
              {loading ? 'Creating…' : 'Create Council Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Admin component ─────────────────────────────────────────────────────
export default function Admin() {
  const [users, setUsers] = useState([]);
  const [councilMembers, setCouncilMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateCouncil, setShowCreateCouncil] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [reminding, setReminding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, sRes, cRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/stats'),
        api.get('/admin/council-members'),
      ]);
      setUsers(uRes.data);
      setStats(sRes.data);
      setCouncilMembers(cRes.data);
    } catch (err) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSendReminders = async () => {
    setReminding(true);
    try {
      const res = await api.post('/admin/send-trial-reminders');
      toast.success(res.data.message);
    } catch {
      toast.error('Failed to send reminders');
    } finally {
      setReminding(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
          <p className="text-gray-500 text-sm mt-0.5">Manage user accounts and access</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSendReminders}
            disabled={reminding}
            className="px-4 py-2 text-sm border border-orange-200 text-orange-700 hover:bg-orange-50 rounded-lg font-medium disabled:opacity-50"
          >
            {reminding ? 'Sending…' : '📧 Send Trial Reminders'}
          </button>
          <button
            onClick={() => setShowCreateCouncil(true)}
            className="px-4 py-2 text-sm border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-lg font-medium"
          >
            + Council Member
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
          >
            + Create User
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard label="Total Users" value={stats.total} color="text-gray-900" />
          <StatCard label="Active" value={stats.active} color="text-green-600" />
          <StatCard label="Blocked" value={stats.blocked} color="text-red-500" />
          <StatCard label="Expiring Soon" value={stats.expiring_soon} color="text-orange-500" />
        </div>
      )}

      {/* Users table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Manager Accounts</h2>
        </div>

        {users.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p className="text-4xl mb-2">👤</p>
            <p>No users yet. Create the first account above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Name', 'Email', 'Company', 'Status', 'Trial Ends', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {users.map(u => {
                  const b = badge(u);
                  return (
                    <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                        {u.first_name} {u.last_name}
                        {u.is_admin && (
                          <span className="ml-1.5 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-semibold">admin</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{u.email}</td>
                      <td className="px-4 py-3 text-gray-500">{u.company || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${b.cls}`}>
                          {b.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {u.trial_ends_at
                          ? new Date(u.trial_ends_at).toLocaleDateString('en-CA')
                          : u.account_type === 'paid' ? '∞' : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setEditUser(u)}
                          className="text-blue-600 hover:text-blue-800 font-medium text-xs"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Council Members */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">Council Member Accounts</h2>
            <p className="text-xs text-gray-400 mt-0.5">Read-only access to their assigned building's data</p>
          </div>
          <button
            onClick={() => setShowCreateCouncil(true)}
            className="text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-medium px-3 py-1.5 rounded-lg transition-colors">
            + Add Member
          </button>
        </div>
        {councilMembers.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <p className="text-3xl mb-2">👥</p>
            <p className="text-sm">No council members yet. Add one above.</p>
            <p className="text-xs mt-1">Council members can view meetings, work orders and financials for their building.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {['Name', 'Email', 'Building', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {councilMembers.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{m.first_name} {m.last_name}</td>
                  <td className="px-4 py-3 text-gray-600">{m.email}</td>
                  <td className="px-4 py-3">
                    {m.building_name ? (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                        {m.building_name}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Remove council access for ${m.first_name} ${m.last_name}?`)) return;
                        try {
                          await api.delete(`/admin/council-members/${m.id}`);
                          setCouncilMembers((p) => p.filter((x) => x.id !== m.id));
                          toast.success('Council member removed');
                        } catch {
                          toast.error('Failed to remove');
                        }
                      }}
                      className="text-red-500 hover:text-red-700 font-medium text-xs">
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={(u) => { setUsers(p => [u, ...p]); load(); }}
        />
      )}
      {showCreateCouncil && (
        <CreateCouncilModal
          onClose={() => setShowCreateCouncil(false)}
          onCreated={(m) => setCouncilMembers((p) => [m, ...p])}
        />
      )}
      {editUser && (
        <EditUserModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onUpdated={(u) => setUsers(p => p.map(x => x.id === u.id ? u : x))}
          onDeleted={(id) => { setUsers(p => p.filter(x => x.id !== id)); load(); }}
        />
      )}
    </div>
  );
}
